// Central API client. The ONLY place the backend URL is referenced.
// Reads REACT_APP_BACKEND_URL (see frontend/.env); change it for deployment
// without touching any component.

import axios from "axios";

// Empty string => same-origin relative "/api" (production single-origin, where
// Express serves the built site + API). Dev sets REACT_APP_BACKEND_URL to the
// Express server (see frontend/.env).
const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

export const api = axios.create({
    baseURL: `${BASE_URL}/api`,
    timeout: 12000,
    headers: { "Content-Type": "application/json" },
});

// ---- Impact records ----
export async function fetchImpact({ category, q } = {}) {
    const params = {};
    if (category && category !== "all") params.category = category;
    if (q) params.q = q;
    const { data } = await api.get("/impact", { params });
    return data; // { results, grouped, count }
}

export async function fetchImpactById(trackingId) {
    const { data } = await api.get(`/impact/${encodeURIComponent(trackingId)}`);
    return data; // full detail record
}

// ---- Drives & Trees (tracker) ----
// Unified lookup: resolves a Drive ID OR a Tree ID. Returns a detail object
// with a `kind` of "drive" or "tree". Throws (404) if nothing matches.
export async function trackById(id) {
    const { data } = await api.get(`/track/${encodeURIComponent(id)}`);
    return data;
}

// Live search — partial matches across drives AND trees. Returns { drives, trees }.
export async function searchPublic(q) {
    if (!q || !q.trim()) return { drives: [], trees: [] };
    const { data } = await api.get("/public/search", { params: { q: q.trim() } });
    return data;
}

export async function fetchPublicDrives({ limit } = {}) {
    const params = {};
    if (limit) params.limit = limit;
    const { data } = await api.get("/public/drives", { params });
    return data; // array of drive cards
}

export async function fetchDriveById(driveId) {
    const { data } = await api.get(`/public/drives/${encodeURIComponent(driveId)}`);
    return data; // full drive detail (kind: "drive")
}

export async function fetchTreeById(treeId) {
    const { data } = await api.get(`/public/trees/${encodeURIComponent(treeId)}`);
    return data; // full tree detail (kind: "tree")
}

// ---- Gallery ----
export async function fetchGallery({ category } = {}) {
    const params = {};
    if (category && category !== "all") params.category = category;
    const { data } = await api.get("/gallery", { params });
    return data; // array of gallery items
}

// ---- Forms ----
export async function submitContact(payload) {
    const { data } = await api.post("/contact", payload);
    return data;
}

export async function submitDonation(payload) {
    const { data } = await api.post("/donations", payload);
    return data;
}

export async function checkHealth() {
    const { data } = await api.get("/health");
    return data;
}

// ---- Homepage stats + recent updates (live aggregation) ----
export async function fetchPublicStats() {
    const { data } = await api.get("/public/stats");
    return data; // { trees, meals, children, drives }
}

export async function fetchRecentUpdates({ limit } = {}) {
    const params = {};
    if (limit) params.limit = limit;
    const { data } = await api.get("/public/updates", { params });
    return data; // array of recent updates
}
